<template lang="pug">
#facility(v-cloak)
  .box(v-if="facility")
    .box-header.with-border.register-header
      .title
        span 施設情報
      .buttons
        button.btn.edit-btn(v-if="_showEditButton" @click="gotoFacilityEdit" type="button") 修正

    .box-body.register-body
      .note
        span 施設情報はご家族・施設関係者がLINEから見ることが出来ます。
      .register-row(v-if="existNoInput")
        .error-messages
          label.error-message 未設定の項目が存在するため、修正ボタンより情報の登録をお願いします。
      FormLabel(key-name="施設名" :value="facility.name")
      FormLabel(key-name="住所" :value="facility.address")
      FormLabel(key-name="電話番号" :value="facility.tel")
      FormLinkLabel(key-name="ホームページ" :value="facility.url" :styles="[{ width: '540px'}]")
      FormImage(v-show="image" key-name="画像" v-model="image" style="width:320px;")

      FormLabel(key-name="施設種別" :value="_facilityCategory")
      FormLabel(key-name="介護度" :value="_careLevels")
      FormLabel(key-name="生活保護" :value="_acceptsWelfare")
      FormLabel(key-name="医療・看護体制" :value="_medicalNursings")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import FacilitiesService, { FacilityCategory, FacilityGetModel } from "@/commons/services/Facilities";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormLinkLabel from "@/pc/components/forms/FormLinkLabel.vue";
import FormImage from "@/pc/components/forms/FormImage.vue";

/**
 * 施設画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    FormLabel,
    FormLinkLabel,
    FormImage
  }
})
export default class Facility extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 施設 */
  facility: FacilityGetModel | null = null;
  /** 施設画像 */
  image: any = "";
  /** 未設定項目が存在するか */
  existNoInput: boolean = false;

  get _showEditButton(): boolean {
    return userStore.user.permissions.functions.facilitiesFunction;
  }

  get _facilityCategory(): string {
    if (this.facility && this.facility.facilityDetailInfo && this.facility.facilityDetailInfo.facilityCategory) {
      switch (this.facility.facilityDetailInfo.facilityCategory) {
        case FacilityCategory.KAIGO_YUURYOU:
          return "介護付有料老人ホーム";
        case FacilityCategory.JYUUTAKU_YUURYOU:
          return "住宅型有料老人ホーム  ";
        case FacilityCategory.SERVICE_JYUUTAKU:
          return "サービス付き高齢者向け住宅";
        case FacilityCategory.CONDOMINIUMS:
          return "シニア向け分譲マンション";
        case FacilityCategory.GROUP_HOME:
          return "グループホーム";
        case FacilityCategory.CARE_HOUSE:
          return "ケアハウス";
        case FacilityCategory.TOKUYO:
          return "特別養護老人ホーム（特養）";
        case FacilityCategory.KAIGO_HOKEN:
          return "介護老人保健施設（老健）";
        case FacilityCategory.MEDICAL_CENTER:
          return "介護医療院";
        case FacilityCategory.OTHER:
          return "その他";
        default:
          return "";
      }
    }

    return "";
  }

  get _careLevels(): string {
    if (this.facility && this.facility.facilityDetailAccept && this.facility.facilityDetailAccept.careLevels) {
      const careLevels: string[] = [];
      if (this.facility.facilityDetailAccept.careLevels.careLevel1000) {
        careLevels.push("自立");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel1010) {
        careLevels.push("申請中");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel2010) {
        careLevels.push("要支援１");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel2020) {
        careLevels.push("要支援２");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel3010) {
        careLevels.push("要介護１");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel3020) {
        careLevels.push("要介護２");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel3030) {
        careLevels.push("要介護３");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel3040) {
        careLevels.push("要介護４");
      }
      if (this.facility.facilityDetailAccept.careLevels.careLevel3050) {
        careLevels.push("要介護５");
      }
      return careLevels.join("、");
    } else {
      return "";
    }
  }

  get _acceptsWelfare(): string {
    if (this.facility && this.facility.facilityDetailAccept) {
      if (this.facility.facilityDetailAccept.acceptsWelfare == true) {
        return "受け入れ可";
      } else if (this.facility.facilityDetailAccept.acceptsWelfare == false) {
        return "受け入れ不可";
      } else {
        return "";
      }
    } else {
      return "";
    }
  }

  get _medicalNursings(): string {
    if (this.facility && this.facility.facilityDetailAccept && this.facility.facilityDetailAccept.medicalNursings) {
      const medicalNursings: string[] = [];
      if (this.facility.facilityDetailAccept.medicalNursings.nursingCareFood) {
        medicalNursings.push("介護食");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.liquidDysphagiaDiet) {
        medicalNursings.push("流動食・嚥下食");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.diabetesInsulin) {
        medicalNursings.push("糖尿病・インスリン");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.gastrostomy) {
        medicalNursings.push("胃ろう");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.nasogastricTube) {
        medicalNursings.push("鼻腔・経管栄養");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.stomaOrColostomy) {
        medicalNursings.push("ストーマ・人工肛門");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.homeOxygenTherapy) {
        medicalNursings.push("在宅酸素療法");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.ventilator) {
        medicalNursings.push("人工呼吸器");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.bedsore) {
        medicalNursings.push("褥瘡・床ずれ");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.phlegmSuction) {
        medicalNursings.push("たん吸引");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.dialysis) {
        medicalNursings.push("人工透析");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.tracheostomy) {
        medicalNursings.push("気管切開");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.totalParenteralNutrition) {
        medicalNursings.push("中心静脈栄養");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.urinaryCatheterOrBalloonCatheter) {
        medicalNursings.push("カテーテル・尿バルーン");
      }
      if (this.facility.facilityDetailAccept.medicalNursings.pacemaker) {
        medicalNursings.push("ペースメーカー");
      }
      return medicalNursings.join("、");
    } else {
      return "";
    }
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.facility = await this.getFacility();
    this.image = await this.convertImageUrl(this.facility.imageUrl);
    this.existNoInput = this.checkNoInput();

    this.isBusy = false;

    return;
  }

  /**
   * 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * DBに保存されているS3URLを公開状態にし、公開状態にした画像のURLを返却する
   */
  public async convertImageUrl(imageUrl: string): Promise<string> {
    // 画像のURLがない場合は空を返す
    if (!imageUrl) {
      return "";
    }

    // 画像URL取得
    const convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);

    return convertImageUrl;
  }

  /**
   * 未設定項目があるかチェックする
   */
  public checkNoInput(): boolean {
    if (this.facility) {
      if (!this.facility.name || !this.facility.address || !this.facility.tel || !this.facility.imageUrl) {
        return true;
      }
    }

    return false;
  }

  /**
   * 施設編集画面に遷移する
   */
  public gotoFacilityEdit(): void {
    this.$router.push(PcUrl.FacilityEdit);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#facility {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  .note {
    margin: 0 20px 20px 0;
  }
  .facility-image {
    border: 1px solid $border-darkgray;
  }
}
</style>
